/**
 * Stats Card Component
 * 
 * Reusable card for displaying statistics with icon, value, and trend.
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  trend?: {
    value: number;
    label: string;
  };
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'slate';
  className?: string;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    icon: 'text-blue-400',
    iconBg: 'bg-blue-500/20',
  },
  green: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    icon: 'text-emerald-400',
    iconBg: 'bg-emerald-500/20',
  },
  purple: {
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
    icon: 'text-purple-400',
    iconBg: 'bg-purple-500/20',
  },
  orange: {
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
    icon: 'text-orange-400',
    iconBg: 'bg-orange-500/20',
  },
  red: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    icon: 'text-red-400',
    iconBg: 'bg-red-500/20',
  },
  slate: {
    bg: 'bg-slate-800/50',
    border: 'border-slate-700',
    icon: 'text-slate-400',
    iconBg: 'bg-slate-700',
  },
};

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon: Icon,
  trend,
  color = 'slate',
  className = '',
}) => {
  const colors = colorClasses[color];

  return (
    <div 
      className={`
        ${colors.bg} ${colors.border}
        border rounded-xl p-5
        transition-all duration-200 hover:scale-[1.02]
        ${className}
      `}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-2">{value}</p>
          
          {trend && (
            <div className="flex items-center gap-1.5 mt-2">
              {trend.value > 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : trend.value < 0 ? (
                <TrendingDown className="w-4 h-4 text-red-400" />
              ) : (
                <Minus className="w-4 h-4 text-slate-400" />
              )}
              <span className={`text-sm font-medium ${
                trend.value > 0 ? 'text-emerald-400' : 
                trend.value < 0 ? 'text-red-400' : 'text-slate-400'
              }`}>
                {trend.value > 0 ? '+' : ''}{trend.value}%
              </span>
              <span className="text-sm text-slate-500">{trend.label}</span>
            </div>
          )}
        </div>
        
        <div className={`${colors.iconBg} p-3 rounded-xl`}>
          <Icon className={`w-6 h-6 ${colors.icon}`} />
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
